import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Breadcrumbs from '../components/Breadcrumbs';
import '../assets/css/CaseStudy.css';
import { FaCalendar, FaBuilding, FaMapMarkerAlt, FaTasks } from 'react-icons/fa';
import { API_BASE_URL, UPLOADS_BASE_URL } from '../config';

const iconMap = {
  sqft: <FaCalendar />,
  type: <FaBuilding />,
  location: <FaMapMarkerAlt />,
  scope: <FaTasks />,
};

const CaseStudy = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}case_studies.php`);
        
        // First check if the response is ok
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        // Try to parse the response as JSON
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.error('Failed to parse JSON:', text);
          throw new Error('Invalid JSON response from server');
        }

        // Check if the response has the expected structure
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid response format');
        }

        if (data.success) {
          if (!Array.isArray(data.data)) {
            throw new Error('Data is not in the expected format');
          }
          setCaseStudies(data.data);
          if (data.data.length === 0) {
            setError('No case studies found');
          }
        } else {
          throw new Error(data.error || 'Failed to load case studies');
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setError(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, []);

  if (loading) {
    return (
      <main className="main-content">
        <div className="loading-container"></div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="case-study-section">
        <div style={{ textAlign: 'center', color: 'red', padding: '40px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Case Studies - Design Craft | Office Furniture Projects & Fitout Portfolio UAE</title>
        <meta 
          name="description" 
          content="Explore Design Craft's portfolio of successful office furniture projects and complete fitouts across UAE. View our case studies featuring executive offices, corporate workspaces, and commercial interior solutions in Dubai, Abu Dhabi, and Sharjah." 
        />
        <meta 
          name="keywords" 
          content="office furniture case studies, office fitout projects UAE, commercial interior portfolio, office design projects Dubai, workspace solutions, corporate furniture installation, office renovation projects, Design Craft portfolio, office furniture supplier UAE" 
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Design Craft" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Case Studies - Design Craft Office Furniture Projects UAE" />
        <meta 
          property="og:description" 
          content="Discover our successful office furniture projects and complete fitouts across UAE. From executive offices to large corporate workspaces, see how we transform business environments." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Design Craft" />
        
        {/* Additional SEO Tags */}
        <meta name="geo.region" content="AE" />
        <meta name="geo.placename" content="Dubai, UAE" />
        
      </Helmet>
      <Breadcrumbs
        title="Case Study"
        links={[
          { text: 'Home', url: '/' },
          { text: 'Case Study', url: '/case-study' },
        ]}
      />
      <div className="case-study-section">
        <h2 className="case-study-heading">Case Study</h2>
        <div className="case-study-cards">
          {caseStudies.map((item, idx) => (
            <div className="case-study-card" key={item.id || idx}>
              <img
                src={item.images && item.images.length > 0 
                  ? `${UPLOADS_BASE_URL}case-studies/${item.images[0]}`
                  : require('../assets/images/products/desks/1.png')}
                alt={item.name}
                className="case-study-img"
                onError={(e) => {
                  e.target.src = require('../assets/images/products/desks/1.png');
                }}
              />
              <div className="case-study-info">
                <h3 className="case-study-title">{item.name}</h3>
                <ul className="case-study-details">
                  <li className="case-study-detail">
                    <span className="case-study-icon">{iconMap.sqft}</span>
                    {item.sqft} SQFT
                  </li>
                  <li className="case-study-detail">
                    <span className="case-study-icon">{iconMap.type}</span>
                    {item.type}
                  </li>
                  <li className="case-study-detail">
                    <span className="case-study-icon">{iconMap.location}</span>
                    {item.location}
                  </li>
                  <li className="case-study-detail">
                    <span className="case-study-icon">{iconMap.scope}</span>
                    SCOPE - {item.scope}
                  </li>
                </ul>
                <Link to={`/case-study/${item.id}`} className="case-study-btn">
                  View Project
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CaseStudy;
